import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { X, Loader2, Copy, Check, AlertCircle, Sparkles, Ticket, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ---- helpers ---- */
const formatTimeLeft = (expiresAt) => {
  try {
    const diffMs = new Date(expiresAt).getTime() - Date.now();
    if (diffMs <= 0) return 'Expired';
    const hrs = Math.floor(diffMs / 3_600_000);
    const mins = Math.floor((diffMs % 3_600_000) / 60_000);
    if (hrs > 0) return `${hrs}h ${mins}m left`;
    return `${mins}m left`;
  } catch { return ''; }
};

const fmt$ = (cents) => `$${(cents / 100).toFixed(2)}`;

const PRESET_OPTIONS = [500, 1000, 2000, 5000];   // $5, $10, $20, $50

/* ---- component ---- */
const RedeemGiftModal = ({ open, onClose, token, balanceCents, onCreated }) => {
  const [amount, setAmount] = useState(1000);
  const [custom, setCustom] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [created, setCreated] = useState(null);   // the gift object after creation
  const [copied, setCopied] = useState('');
  const [, force] = useState(0);

  // Re-render the countdown every 30 seconds while a gift is active
  useEffect(() => {
    if (!created) return;
    const id = setInterval(() => force((n) => n + 1), 30_000);
    return () => clearInterval(id);
  }, [created]);

  // Reset when reopened
  useEffect(() => {
    if (open) {
      setAmount(1000);
      setCustom('');
      setError('');
      setCreated(null);
    }
  }, [open]);

  const selectedAmount = useMemo(() => {
    if (custom.trim()) {
      const n = parseFloat(custom);
      if (!isNaN(n) && n > 0) return Math.round(n * 100);
    }
    return amount;
  }, [amount, custom]);

  const overBalance = selectedAmount > balanceCents;

  const onCreate = async () => {
    setError('');
    if (selectedAmount < 100) { setError('Minimum redeem is $1.'); return; }
    if (overBalance) { setError(`You only have ${fmt$(balanceCents)} available.`); return; }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/customer/gift-redemptions/create`,
        { amount_cents: selectedAmount },
        { headers: { Authorization: `Bearer ${token}` } });
      setCreated(r.data);
      onCreated?.(r.data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not create gift.');
    } finally { setLoading(false); }
  };

  const onCancel = async () => {
    if (!created || !window.confirm('Cancel this gift and return the credit to your balance?')) return;
    setLoading(true);
    try {
      await axios.post(`${API}/customer/gift-redemptions/${created.gift_id}/cancel`, {},
        { headers: { Authorization: `Bearer ${token}` } });
      setCreated(null);
      onCreated?.(null);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not cancel.');
    } finally { setLoading(false); }
  };

  const onCopy = async (text, kind) => {
    try { await navigator.clipboard.writeText(text); setCopied(kind); setTimeout(() => setCopied(''), 1500); } catch {}
  };

  if (!open) return null;

  // ----------------- ACTIVE GIFT VIEW -----------------
  if (created) {
    const qrUrl = `https://quickchart.io/qr?text=${encodeURIComponent(created.qr_payload)}&size=320&margin=2&ecLevel=M`;
    return (
      <Backdrop onClose={onClose}>
        <Card>
          <ModalHeader title="Your gift is ready" onClose={onClose} />
          <div className="p-5 sm:p-7 text-center" data-testid="active-gift-view">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-emerald-500/40 bg-emerald-500/[0.08] text-emerald-300 text-[10.5px] uppercase tracking-[0.18em] font-bold">
              <Sparkles className="w-3 h-3" /> Active · {formatTimeLeft(created.expires_at)}
            </div>
            <div className="mt-4 mb-5 mx-auto bg-white p-4 rounded-2xl inline-block" data-testid="gift-qr-img">
              <img src={qrUrl} alt="Gift QR" className="w-48 h-48 sm:w-56 sm:h-56 block" />
            </div>
            <div className="text-5xl font-extrabold text-yellow-300 tracking-tight tabular-nums">
              {fmt$(created.amount_cents)}
            </div>
            <p className="mt-2 text-[12.5px] text-white/55">Show this QR or share the code with a participating restaurant.</p>

            <div className="mt-5 grid grid-cols-1 gap-2">
              <button onClick={() => onCopy(created.code, 'code')} data-testid="copy-gift-code-btn"
                className="flex items-center justify-between gap-2 px-4 py-3 rounded-xl border border-white/15 hover:border-yellow-500/50 bg-black/40 transition-all">
                <span className="text-[10.5px] uppercase tracking-[0.18em] text-white/55 font-bold">Code</span>
                <span className="font-mono text-[16px] font-bold text-white tracking-[0.2em]">{created.code}</span>
                {copied === 'code' ? <Check className="w-4 h-4 text-emerald-300" /> : <Copy className="w-4 h-4 text-white/55" />}
              </button>
              <button onClick={() => onCopy(created.gift_id, 'gift')}
                className="flex items-center justify-between gap-2 px-4 py-3 rounded-xl border border-white/10 hover:border-yellow-500/35 bg-black/40 transition-all">
                <span className="text-[10.5px] uppercase tracking-[0.18em] text-white/55 font-bold">Gift ID</span>
                <span className="font-mono text-[13px] text-white/75">{created.gift_id}</span>
                {copied === 'gift' ? <Check className="w-4 h-4 text-emerald-300" /> : <Copy className="w-4 h-4 text-white/45" />}
              </button>
            </div>

            <div className="mt-5 flex flex-col gap-2">
              <button onClick={onClose} data-testid="gift-done-btn"
                className="w-full py-3 rounded-xl bg-gradient-to-b from-yellow-300 to-yellow-500 text-black font-bold text-[14px]">
                Done — I'll use it later
              </button>
              <button onClick={onCancel} disabled={loading} data-testid="gift-cancel-btn"
                className="w-full py-2.5 rounded-xl text-[12.5px] text-white/55 hover:text-rose-300 transition-colors">
                {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin mx-auto" /> : 'Cancel and refund'}
              </button>
            </div>
          </div>
        </Card>
      </Backdrop>
    );
  }

  // ----------------- AMOUNT-PICKER VIEW -----------------
  return (
    <Backdrop onClose={onClose}>
      <Card>
        <ModalHeader title="Use your gift credits" onClose={onClose} />
        <div className="p-5 sm:p-7" data-testid="redeem-amount-view">
          <div className="text-center">
            <div className="text-[11px] uppercase tracking-[0.22em] text-white/45 font-bold">Available balance</div>
            <div className="mt-1 text-3xl font-extrabold text-yellow-300 tabular-nums" data-testid="redeem-balance">{fmt$(balanceCents)}</div>
          </div>

          <p className="mt-4 text-[12.5px] text-white/55 leading-relaxed text-center">
            Pick how much you want to use. We'll generate a QR + code valid for 24 hours.
          </p>

          <div className="mt-5 grid grid-cols-4 gap-2" data-testid="redeem-preset-options">
            {PRESET_OPTIONS.map((p) => (
              <button key={p} onClick={() => { setAmount(p); setCustom(''); }}
                disabled={p > balanceCents}
                data-testid={`redeem-preset-${p}`}
                className={`py-3 rounded-xl border text-[13px] font-bold transition-all
                  ${amount === p && !custom
                    ? 'border-yellow-500/60 bg-yellow-500/[0.10] text-yellow-200'
                    : 'border-white/12 hover:border-white/30 text-white/85 disabled:opacity-30 disabled:cursor-not-allowed'}`}>
                ${p / 100}
              </button>
            ))}
          </div>

          <label className="block mt-4">
            <span className="block text-[10.5px] uppercase tracking-[0.18em] text-white/45 font-bold mb-1.5">
              Custom amount
            </span>
            <div className="relative">
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/55 font-bold">$</span>
              <input
                type="number"
                inputMode="decimal"
                min="1"
                step="0.01"
                value={custom}
                onChange={(e) => setCustom(e.target.value)}
                placeholder="0.00"
                data-testid="redeem-custom-input"
                className="w-full pl-7 pr-4 py-3 rounded-xl bg-black/40 border border-white/12 focus:border-yellow-500/50 outline-none text-[15px] text-white"
              />
            </div>
          </label>

          {error && (
            <div className="mt-3 flex items-start gap-2 p-3 rounded-xl border border-rose-500/30 bg-rose-500/[0.06] text-rose-200 text-[12.5px]" data-testid="redeem-error">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <button onClick={onCreate} disabled={loading || overBalance || selectedAmount < 100}
            data-testid="redeem-create-btn"
            className="mt-5 w-full inline-flex items-center justify-center gap-2 py-4 rounded-xl bg-gradient-to-b from-yellow-300 to-yellow-500 hover:from-yellow-200 hover:to-yellow-400 disabled:opacity-50 disabled:cursor-not-allowed text-black font-extrabold text-[15px]">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ticket className="w-4 h-4" />}
            Generate gift for {fmt$(selectedAmount)}
          </button>

          <p className="mt-4 text-[11px] text-white/40 text-center leading-relaxed">
            <Clock className="w-3 h-3 inline mr-1 -mt-0.5" /> Gift expires in 24 hours. Unused gifts auto-refund.
          </p>
        </div>
      </Card>
    </Backdrop>
  );
};

/* ---- shell pieces ---- */
const Backdrop = ({ onClose, children }) => (
  <div
    className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
    onClick={onClose}
    data-testid="redeem-modal-backdrop"
  >
    <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md">
      {children}
    </div>
  </div>
);

const Card = ({ children }) => (
  <div className="rounded-2xl border border-yellow-500/30 bg-zinc-950 text-white overflow-hidden shadow-[0_20px_60px_-12px_rgba(0,0,0,0.7)]">
    {children}
  </div>
);

const ModalHeader = ({ title, onClose }) => (
  <div className="flex items-center justify-between px-5 sm:px-7 py-4 border-b border-white/[0.07]">
    <h3 className="text-[14px] font-extrabold tracking-tight">{title}</h3>
    <button onClick={onClose} aria-label="Close" data-testid="redeem-modal-close"
      className="w-8 h-8 rounded-lg border border-white/12 hover:border-white/30 text-white/65 hover:text-white flex items-center justify-center transition-all">
      <X className="w-4 h-4" />
    </button>
  </div>
);

export default RedeemGiftModal;
